import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import BotaoDuplo from '../../../Componentes/BotaoDuplo';
import CampoEdicoes from '../../../Componentes/CampoEdicoes';
import CorpoPagina from '../../../Componentes/CorpoPagina';
import ModalConfirmacao from '../../../Componentes/ModalConfirmacao';
import SelectInput from '../../../Componentes/SelectInput';
import TextInput from '../../../Componentes/TextInput';
import TopicoTexto from '../../../Componentes/TopicoTexto';
import { apiService } from '../../../Services/ApiService';
import useWindowSize from '../../../Hooks/windowSize';
import { useTranslation } from 'react-i18next';

export default function AtuadorCadastro() {
  const windowSize = useWindowSize();
  const [isMobile, setIsMobile] = useState(false);
  const [isMedium, setIsMedium] = useState(false);
  const { t } = useTranslation('errors');
  const [actuator, setActuator] = useState({
    nameCadastro: '',
    actuatorIdCadastro: '',
    ambienteCadastro: '',
  });
  const [initialActuatorState, setInitialActuatorState] = useState({ ...actuator });
  const [environments, setEnvironments] = useState([]);

  const [validationErrors, setValidationErrors] = useState({
    nameCadastro: false,
    actuatorIdCadastro: false,
    ambienteCadastro: false,
  });
  const [isEditing, setIsEditing] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [modalConfirm, setModalConfirm] = useState(false);
  const [modalSuccess, setModalSuccess] = useState(false);
  const [modalError, setModalError] = useState({
    visible: false,
    message: '',
  });
  const [modalCancel, setModalCancel] = useState(false);
  const { id } = useParams();
  const { get, post, put } = apiService();
  const navigate = useNavigate();
  const [environmentsLoaded, setEnvironmentsLoaded] = useState(false);

  useEffect(() => {
    setIsMobile(windowSize.width < 768);
    setIsMedium(windowSize.width > 767 && windowSize.width < 1024);
  }, [windowSize]);

  useEffect(() => {
    if (id) {
      getActuatorById();
      setIsEditing(true);
    } else {
      setIsLoading(false);
      setIsEditing(false);
    }
  }, [id]);

  const getActuatorById = async () => {
    try {
      setIsLoading(true);
      const actuator = await get(`actuators/${id}`);
      if (actuator) {
        setActuator({
          nameCadastro: actuator.name,
          actuatorIdCadastro: actuator.identifier,
          ambienteCadastro: { value: actuator.environment.id, label: actuator.environment.name },
        });

        setIsFormValid(true);
      }
    } catch (error) {
      setModalError({ visible: true, message: 'Erro ao buscar atuador' });
    } finally {
      setIsLoading(false);
    }
  };

  const validators = {
    nameCadastro: (value) => value && value.trim().length > 0 && value.trim().length <= 100,
    actuatorIdCadastro: (value) => value && value.trim().length > 0 && value.trim().length <= 25,
  };

  const validateField = (name, value) => {
    if (validators[name]) {
      return validators[name](value);
    }
    return true;
  };

  const validateAllFieldsStates = (validationErrors) => {
    const isValid = Object.keys(validationErrors).every((key) => !validationErrors[key]);

    const isFilled = () => {
      for (const key in actuator) {
        if (
          actuator.hasOwnProperty(key) &&
          typeof actuator[key] === 'string' &&
          actuator[key].trim() === ''
        ) {
          return false;
        }
      }
      return true;
    };

    if (isEditing) {
      setIsFormValid(isValid && isFilled() && checkForChanges());
    } else {
      setIsFormValid(isValid && isFilled());
    }
  };

  const handleChange = (name, value) => {
    const isValid = validateField(name, value);
    const newValidationErrors = { ...validationErrors, [name]: !isValid };
    setActuator((prev) => ({ ...prev, [name]: value }));
    setValidationErrors(newValidationErrors);

    validateAllFieldsStates(newValidationErrors);
  };

  const handleSave = () => {
    setModalConfirm(true);
  };

  const saveActuator = async () => {
    if (!isFormValid) {
      setModalConfirm(false);
      return;
    }

    const data = {
      id,
      name: actuator.nameCadastro,
      identifier: actuator.actuatorIdCadastro,
      environmentId: actuator.ambienteCadastro.value,
    };

    try {
      setIsLoading(true);
      if (id) {
        await put(`actuators/${id}`, data);
      } else {
        await post('actuators', data);
      }

      setModalSuccess(true);
    } catch (error) {
      let errorMessage = 'Erro ao salvar atuador';
      if (error.message) {
        errorMessage = t(error.message);
      } else if (error.response && error.response.data && error.response.data.message) {
        errorMessage = t(`errors.${error.response.data.message}`);
      }

      setModalError({
        visible: true,
        message: errorMessage,
      });
    } finally {
      setModalConfirm(false);
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setModalCancel(true);
  };

  const checkForChanges = () => {
    for (const key in actuator) {
      if (actuator.hasOwnProperty(key) && actuator[key] !== initialActuatorState[key]) {
        return true;
      }
    }
    return false;
  };

  useEffect(() => {
    getEnvironments();
  }, []);

  useEffect(() => {
    validateAllFieldsStates(validationErrors);
  }, [validationErrors]);

  useEffect(() => {
    setInitialActuatorState({ ...actuator });
  }, [actuator]);

  const getEnvironments = async () => {
    setIsLoading(true);
    try {
      const data = await get('environments/all');
      if (data && data.length) {
        const environmentsList = data || [];
        setEnvironments(environmentsList.map((env) => ({ value: env.id, label: env.name })));
        setEnvironmentsLoaded(true);
      }
    } catch (error) {
      setModalError({ visible: true, message: 'Erro ao buscar ambientes' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <CorpoPagina loading={isLoading}>
      {modalCancel && (
        <ModalConfirmacao
          aviso="Deseja realmente cancelar?"
          confirmar={false}
          clicado={() => navigate(-1)}
          clicadoVolta={() => setModalCancel(false)}
        />
      )}
      {modalSuccess && (
        <ModalConfirmacao
          aviso="Atuador salvo com sucesso!"
          confirmar={true}
          clicado={() => navigate(-1)}
        />
      )}
      {modalConfirm && (
        <ModalConfirmacao
          aviso="Deseja realmente salvar o atuador?"
          confirmar={false}
          clicado={saveActuator}
          clicadoVolta={() => setModalConfirm(false)}
        />
      )}
      {modalError.visible && (
        <ModalConfirmacao
          aviso={modalError.message}
          erro={true}
          clicado={() => setModalError({ visible: false, message: '' })}
        />
      )}
      <div className="ContainerGeral">
        {!isEditing && <TopicoTexto titulo="Cadastrar Atuador" />}
        {isEditing && <TopicoTexto titulo="Editar Atuador" />}
        <div className="MeioLinha">
          <div style={{ width: isMobile || isMedium ? '-webkit-fill-available' : '35%' }}>
            <CampoEdicoes titulo="DADOS GERAIS" maxH={505} height={505}>
              <TextInput
                titulo="Nome"
                padrao={actuator.nameCadastro}
                placeholder="Insira o Nome"
                tipo="input"
                largura="90%"
                top={8}
                onUpdate={(txt) => handleChange('nameCadastro', txt)}
                erro={validationErrors.nameCadastro}
                erroTexto="Nome inválido"
                maxLength={100}
              />

              <TextInput
                titulo="ID"
                padrao={actuator.actuatorIdCadastro}
                placeholder="Insira o ID"
                tipo="input"
                largura="90%"
                top={8}
                onUpdate={(txt) => handleChange('actuatorIdCadastro', txt)}
                erro={validationErrors.actuatorIdCadastro}
                erroTexto="ID inválido"
                maxLength={25}
              />

              {environmentsLoaded ? (
                <SelectInput
                  titulo="Ambiente"
                  itens={environments}
                  padrao={
                    actuator.ambienteCadastro
                      ? actuator.ambienteCadastro
                      : { value: '', label: 'Selecione' }
                  }
                  largura="90%"
                  clicado={(txt) => handleChange('ambienteCadastro', txt)}
                />
              ) : (
                <div></div>
              )}
            </CampoEdicoes>
          </div>
        </div>

        <BotaoDuplo
          nome="Salvar"
          nomeVolta="Cancelar"
          bottom={32}
          top={16}
          clicado={handleSave}
          clicadoVolta={handleCancel}
          disabled={!isFormValid}
          externo={true}
        />
      </div>
    </CorpoPagina>
  );
}
